import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import Layout from '../components/layout/Layout';
import SlidePanel from '../components/common/SlidePanel';
import Badge from '../components/common/Badge';
import { Input, Select, Textarea } from '../components/common/FormField';
import PatientSearchInput from '../components/patient/PatientSearchInput';
import FileShaClaimButton from '../components/common/FileShaClaimButton';
import api from '../services/api';
import { fmtDate, fmtDateTime } from '../utils/helpers';
import {useTranslation} from 'react-i18next';

const TABS = [['access', '🩸 Vascular Access'], ['sessions', '💉 Dialysis Sessions']];
const ACCESS_TYPES = ['AVF', 'AVG', 'CENTRAL_VENOUS_CATHETER', 'PERITONEAL_CATHETER'];
const SESSION_TYPES = ['HEMODIALYSIS', 'PERITONEAL_DIALYSIS', 'HEMODIAFILTRATION'];

export default function RenalCarePage() {
  const {t}=useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState('access');
  const [stats, setStats] = useState<any>({});
  const [patient, setPatient] = useState<any>(null);
  const [accesses, setAccesses] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);
  const [sessionCount, setSessionCount] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const notify = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 5000); };

  const [showAccess, setShowAccess] = useState(false);
  const defaultAccessForm = { patientId: '', accessType: 'AVF', site: '', creationDate: '', status: 'ACTIVE', complications: '', notes: '' };
  const [accessForm, setAccessForm] = useState<any>(defaultAccessForm);

  const [showSession, setShowSession] = useState(false);
  const defaultSessionForm = { patientId: '', vascularAccessId: '', doctorId: '', sessionDate: new Date().toISOString().slice(0, 16), sessionType: 'HEMODIALYSIS', durationMinutes: 240, preWeightKg: '', postWeightKg: '', preBp: '', postBp: '', ultrafiltrationVolumeMl: '', dialyzerType: '', bloodFlowRate: '', complications: '', notes: '', status: 'COMPLETED' };
  const [sessionForm, setSessionForm] = useState<any>(defaultSessionForm);
  const [lastCreatedSession, setLastCreatedSession] = useState<any>(null);

  const loadStats = useCallback(async () => {
    try { const r = await api.getRenalStats(); setStats(r?.data || {}); } catch { }
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const loadAccesses = useCallback(async (patientId: string) => {
    try {
      const r = await api.getVascularAccesses(patientId);
      setAccesses(Array.isArray(r?.data) ? r.data : (r?.data?.data || []));
    } catch { setAccesses([]); }
  }, []);

  const loadSessions = useCallback(async (patientId?: string) => {
    try {
      const r = await api.getDialysisSessions(patientId ? { patientId } : {});
      setSessions(Array.isArray(r?.data) ? r.data : (r?.data?.data || []));
    } catch { setSessions([]); }
  }, []);

  const loadSessionCount = useCallback(async (patientId: string) => {
    try { const r = await api.getDialysisSessionCount(patientId); setSessionCount(r?.data || null); } catch { setSessionCount(null); }
  }, []);

  useEffect(() => {
    if (patient) {
      loadAccesses(patient.id);
      loadSessions(patient.id);
      loadSessionCount(patient.id);
      setLastCreatedSession(null);
    } else {
      setAccesses([]);
      setSessionCount(null);
      loadSessions();
    }
  }, [patient, loadAccesses, loadSessions, loadSessionCount]);

  const saveAccess = async () => {
    if (!patient) { notify('⚠️ Select a patient first'); return; }
    if (!accessForm.site) { notify('⚠️ Access site required'); return; }
    setSaving(true);
    try {
      await api.createVascularAccess({ ...accessForm, patientId: patient.id });
      notify('✅ Vascular access recorded!'); setShowAccess(false); setAccessForm(defaultAccessForm);
      loadAccesses(patient.id); loadStats();
    } catch (e: any) { notify('❌ ' + (e.response?.data?.message || 'Error')); }
    setSaving(false);
  };

  const saveSession = async () => {
    if (!patient) { notify('⚠️ Select a patient first'); return; }
    setSaving(true);
    try {
      const r = await api.createDialysisSession({ ...sessionForm, patientId: patient.id, doctorId: user?.id });
      const created = r?.data;
      setLastCreatedSession(created);
      notify(created?.needsPreauthCheckpoint ? `⚠️ Session #${created.sessionNumberForClaim} recorded — SHA PREAUTH CHECKPOINT required for this session!` : `✅ Session #${created?.sessionNumberForClaim} recorded`);
      setShowSession(false); setSessionForm(defaultSessionForm);
      loadSessions(patient.id); loadSessionCount(patient.id); loadStats();
    } catch (e: any) { notify('❌ ' + (e.response?.data?.message || 'Error')); }
    setSaving(false);
  };

  return (
    <Layout title="Renal Care (Dialysis)" actions={
      <div className="flex gap-2">
        <button onClick={() => { setAccessForm(defaultAccessForm); setShowAccess(true); }} className="btn-secondary text-sm">🩸 New Vascular Access</button>
        <button onClick={() => { if (!patient) { notify('⚠️ Select a patient first'); return; } setSessionForm({ ...defaultSessionForm, vascularAccessId: accesses[0]?.id || '' }); setShowSession(true); }} className="btn-primary text-sm">+ New Dialysis Session</button>
      </div>
    }>
      {msg && <div className="fixed top-4 right-4 bg-white border shadow-xl rounded-xl px-4 py-3 text-sm z-50 max-w-sm">{msg}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {[['Sessions Today', stats.sessionsToday ?? '—', 'blue'], ['Active Patients (30d)', stats.activePatients30d ?? '—', 'purple'], ['Active Vascular Accesses', stats.activeVascularAccesses ?? '—', 'green']].map(([l, v, c]) => (
          <div key={String(l)} className="card p-3 text-center"><p className="text-xs text-slate-400">{l}</p><p className={`text-2xl font-bold text-${c}-600`}>{v}</p></div>
        ))}
      </div>

      <div className="card p-3 mb-4">
        <PatientSearchInput label="Patient" selected={patient} onSelect={setPatient} onClear={() => setPatient(null)} />
      </div>

      {patient && sessionCount && (
        <div className={`rounded-xl p-3 mb-4 text-sm border ${sessionCount.needsPreauthCheckpoint ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
          Sessions so far: <strong>{sessionCount.totalSessions}</strong> · Next session number: <strong>{sessionCount.nextSessionNumber}</strong>
          {sessionCount.needsPreauthCheckpoint && <span className="ml-2 font-semibold">⚠️ Next session requires SHA preauth checkpoint (3rd new-case / every 26th continuing-case session)</span>}
        </div>
      )}

      {lastCreatedSession?.needsPreauthCheckpoint && (
        <div className="bg-red-50 border border-red-300 rounded-xl p-3 mb-4 text-sm text-red-700 flex items-center justify-between gap-3 flex-wrap">
          <span>⚠️ Session #{lastCreatedSession.sessionNumberForClaim} just recorded requires an SHA preauthorization checkpoint before billing/claim submission.</span>
          {patient && (
            <FileShaClaimButton
              patientId={patient.id}
              patientName={`${patient.firstName || patient.first_name || ''} ${patient.lastName || patient.last_name || ''}`.trim()}
              patientCrId={patient.sha_beneficiary_cr_id || patient.shaBeneficiaryCrId}
              encounterId={lastCreatedSession.id}
              serviceName="Hemodialysis"
              from="renal"
              className="text-xs px-3 py-1.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-all w-fit whitespace-nowrap"
            />
          )}
        </div>
      )}

      <div className="flex gap-1 mb-4 bg-slate-100 rounded-xl p-1 overflow-x-auto">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`flex-shrink-0 text-sm py-2 px-3 rounded-lg transition-all ${tab === k ? 'bg-white shadow font-semibold text-[#02357A]' : 'text-slate-600 hover:text-slate-800'}`}>{l}</button>
        ))}
      </div>

      {tab === 'access' && (
        !patient ? (
          <div className="card p-12 text-center text-slate-400"><span className="text-4xl block mb-2">🩸</span>Select a patient above to view/record vascular access</div>
        ) : (
          <div className="card overflow-hidden">
            <div className="p-3 bg-slate-50 border-b grid grid-cols-5 text-xs font-semibold text-slate-500 uppercase gap-2">
              <span>{t('common.type','Type')}</span><span>Site</span><span>Created</span><span>{t('common.status','Status')}</span><span>Complications</span>
            </div>
            {accesses.length === 0 ? <div className="p-8 text-center text-slate-400">No vascular access recorded</div> :
              <div className="divide-y max-h-[60vh] overflow-y-auto">
                {accesses.map((a: any) => (
                  <div key={a.id} className="grid grid-cols-5 gap-2 p-3 items-center hover:bg-slate-50 text-sm">
                    <span className="font-medium">{a.access_type || a.accessType}</span>
                    <span>{a.site || '—'}</span>
                    <span className="text-xs text-slate-500">{a.creation_date || a.creationDate ? fmtDate(a.creation_date || a.creationDate) : '—'}</span>
                    <Badge label={a.status} className={a.status === 'ACTIVE' ? 'bg-green-100 text-green-700 text-xs' : 'bg-slate-100 text-slate-500 text-xs'} />
                    <span className="text-xs text-red-500">{a.complications || '—'}</span>
                  </div>
                ))}
              </div>}
          </div>
        )
      )}

      {tab === 'sessions' && (
        <div className="card overflow-hidden">
          {patient && sessions.length > 0 && (
            <div className="p-2 bg-blue-50/50 border-b flex justify-end">
              <FileShaClaimButton
                patientId={patient.id}
                patientName={`${patient.firstName || patient.first_name || ''} ${patient.lastName || patient.last_name || ''}`.trim()}
                patientCrId={patient.sha_beneficiary_cr_id || patient.shaBeneficiaryCrId}
                encounterId={sessions[0].id}
                serviceName="Hemodialysis"
              />
            </div>
          )}
          <div className="p-3 bg-slate-50 border-b grid grid-cols-8 text-xs font-semibold text-slate-500 uppercase gap-2">
            {!patient && <span>{t('common.patient','Patient')}</span>}
            <span>{t('common.date','Date')}</span><span>{t('common.type','Type')}</span><span>Session #</span><span>Duration</span><span>Pre/Post BP</span><span>UF Volume</span><span>{t('common.status','Status')}</span>
          </div>
          {sessions.length === 0 ? <div className="p-8 text-center text-slate-400">No dialysis sessions recorded</div> :
            <div className="divide-y max-h-[60vh] overflow-y-auto">
              {sessions.map((s: any) => (
                <div key={s.id} className="grid grid-cols-8 gap-2 p-3 items-center hover:bg-slate-50 text-sm">
                  {!patient && <span className="text-xs">{s.first_name} {s.last_name}</span>}
                  <span className="text-xs">{fmtDateTime(s.session_date || s.sessionDate)}</span>
                  <span className="text-xs">{s.session_type || s.sessionType}</span>
                  <span className="font-semibold">
                    #{s.session_number_for_claim || s.sessionNumberForClaim}
                    {(s.session_number_for_claim === 3 || s.sessionNumberForClaim === 3) && <span title="SHA preauth checkpoint" className="ml-1">⚠️</span>}
                  </span>
                  <span className="text-xs">{s.duration_minutes || s.durationMinutes} min</span>
                  <span className="text-xs">{s.pre_bp || s.preBp || '—'} / {s.post_bp || s.postBp || '—'}</span>
                  <span className="text-xs">{s.ultrafiltration_volume_ml || s.ultrafiltrationVolumeMl || '—'} ml</span>
                  <Badge label={s.status} className={s.status === 'COMPLETED' ? 'bg-green-100 text-green-700 text-xs' : 'bg-amber-100 text-amber-700 text-xs'} />
                </div>
              ))}
            </div>}
        </div>
      )}

      <SlidePanel isOpen={showAccess} onClose={() => setShowAccess(false)} title="New Vascular Access" icon="🩸"
        actions={<button onClick={saveAccess} disabled={saving} className="btn-primary w-full">{saving ? 'Saving...' : 'Save Access'}</button>}>
        <div className="space-y-3">
          {patient && <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-sm text-[#02357A]">Patient: <strong>{patient.firstName || patient.first_name} {patient.lastName || patient.last_name}</strong></div>}
          <div><label className="label">Access Type</label>
            <Select value={accessForm.accessType} onChange={e => setAccessForm({ ...accessForm, accessType: e.target.value })}>
              {ACCESS_TYPES.map(t => <option key={t}>{t}</option>)}
            </Select>
          </div>
          <div><label className="label">Site *</label><Input value={accessForm.site} onChange={e => setAccessForm({ ...accessForm, site: e.target.value })} placeholder="e.g. Left radiocephalic" /></div>
          <div><label className="label">Creation Date</label><Input type="date" value={accessForm.creationDate} onChange={e => setAccessForm({ ...accessForm, creationDate: e.target.value })} /></div>
          <div><label className="label">{t('common.status','Status')}</label>
            <Select value={accessForm.status} onChange={e => setAccessForm({ ...accessForm, status: e.target.value })}>
              {['ACTIVE', 'FAILED', 'REMOVED', 'MATURING'].map(s => <option key={s}>{s}</option>)}
            </Select>
          </div>
          <div><label className="label">Complications</label><Textarea rows={2} value={accessForm.complications} onChange={e => setAccessForm({ ...accessForm, complications: e.target.value })} /></div>
          <div><label className="label">{t('common.notes','Notes')}</label><Textarea rows={2} value={accessForm.notes} onChange={e => setAccessForm({ ...accessForm, notes: e.target.value })} /></div>
        </div>
      </SlidePanel>

      <SlidePanel isOpen={showSession} onClose={() => setShowSession(false)} title="New Dialysis Session" icon="💉" size="lg"
        actions={<button onClick={saveSession} disabled={saving} className="btn-primary w-full">{saving ? 'Saving...' : 'Save Session'}</button>}>
        <div className="space-y-3">
          {patient && <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-sm text-[#02357A]">Patient: <strong>{patient.firstName || patient.first_name} {patient.lastName || patient.last_name}</strong></div>}
          {sessionCount && <div className="text-xs text-slate-500">This will be session #{sessionCount.nextSessionNumber} for claim purposes.</div>}
          {accesses.length > 0 && (
            <div><label className="label">Vascular Access</label>
              <Select value={sessionForm.vascularAccessId} onChange={e => setSessionForm({ ...sessionForm, vascularAccessId: e.target.value })}>
                <option value="">— None —</option>
                {accesses.map((a: any) => <option key={a.id} value={a.id}>{a.access_type || a.accessType} — {a.site}</option>)}
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Session Date/Time</label><Input type="datetime-local" value={sessionForm.sessionDate} onChange={e => setSessionForm({ ...sessionForm, sessionDate: e.target.value })} /></div>
            <div><label className="label">Session Type</label>
              <Select value={sessionForm.sessionType} onChange={e => setSessionForm({ ...sessionForm, sessionType: e.target.value })}>
                {SESSION_TYPES.map(t => <option key={t}>{t}</option>)}
              </Select>
            </div>
          </div>
          <div><label className="label">Duration (minutes)</label><Input type="number" value={sessionForm.durationMinutes} onChange={e => setSessionForm({ ...sessionForm, durationMinutes: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Pre Weight (kg)</label><Input type="number" step="0.1" value={sessionForm.preWeightKg} onChange={e => setSessionForm({ ...sessionForm, preWeightKg: e.target.value })} /></div>
            <div><label className="label">Post Weight (kg)</label><Input type="number" step="0.1" value={sessionForm.postWeightKg} onChange={e => setSessionForm({ ...sessionForm, postWeightKg: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Pre BP</label><Input value={sessionForm.preBp} onChange={e => setSessionForm({ ...sessionForm, preBp: e.target.value })} placeholder="130/85" /></div>
            <div><label className="label">Post BP</label><Input value={sessionForm.postBp} onChange={e => setSessionForm({ ...sessionForm, postBp: e.target.value })} placeholder="120/80" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Ultrafiltration Volume (ml)</label><Input type="number" value={sessionForm.ultrafiltrationVolumeMl} onChange={e => setSessionForm({ ...sessionForm, ultrafiltrationVolumeMl: e.target.value })} /></div>
            <div><label className="label">Blood Flow Rate</label><Input type="number" value={sessionForm.bloodFlowRate} onChange={e => setSessionForm({ ...sessionForm, bloodFlowRate: e.target.value })} /></div>
          </div>
          <div><label className="label">Dialyzer Type</label><Input value={sessionForm.dialyzerType} onChange={e => setSessionForm({ ...sessionForm, dialyzerType: e.target.value })} /></div>
          <div><label className="label">Complications</label><Textarea rows={2} value={sessionForm.complications} onChange={e => setSessionForm({ ...sessionForm, complications: e.target.value })} /></div>
          <div><label className="label">{t('common.notes','Notes')}</label><Textarea rows={2} value={sessionForm.notes} onChange={e => setSessionForm({ ...sessionForm, notes: e.target.value })} /></div>
        </div>
      </SlidePanel>
    </Layout>
  );
}
